import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { icon } from "../../../config";
import CommonTabStrip from "../../../shared/components/CommonTabStrip/CommonTabStrip";
import { getWorkflowFormById } from "../../../shared/services/workflow.services";
import { FORM_TABS } from "../FormsConstant";
import { FormCalculationProvider } from "../contexts/FormCalculationContext";
import { useFormExpressionBuilder } from "../hooks/useFormExpressionBuilder";
import { mergeSmartChipsIntoFormData } from "../utils/smartChipsUtils";
import "./FormPreview.scss";
import FormRenderer from "./FormRenderer";

const FormPreviewContent = ({ isOpen, onClose, formData, formTitle = "Untitled Form", formDescription = "", formId, formTab, setFormTab }) => {
  if (!isOpen) return null;
  const [realTimeFormData, setRealTimeFormData] = useState(formData);
  const [formResponses, setFormResponses] = useState({});
  const [smartChipResponses, setSmartChipResponses] = useState({});
  // Use expression builder hook to handle expression evaluation for calculated values
  useFormExpressionBuilder(realTimeFormData, formResponses, setFormResponses, isOpen, formId, formTab);

  // Handle field changes to allow expression testing in preview mode
  const handleFieldChange = useCallback((fieldId, value, smartChipId) => {
    setFormResponses((prev) => {
      if (prev[fieldId] === value) return prev;
      return { ...prev, [fieldId]: value };
    });
    if (smartChipId) {
      setSmartChipResponses((prev) => {
        if (prev[smartChipId] === value) return prev;
        return { ...prev, [smartChipId]: value };
      });
    }
  }, []);

  useEffect(() => {
    if (formId) {
      getWorkflowFormById({ id: formId }).then((response) => {
        // If smartChips exist (new structure), merge them into form data
        // Otherwise use old merge logic for backward compatibility
        let updatedFormData;
        if (response?.content?.smartChips && Object.keys(response?.content.smartChips).length > 0) {
          // Use formData from props if available, otherwise use response.content
          const baseFormData = formData ?? response?.content;
          baseFormData.smartChips = { ...(baseFormData?.smartChips ?? {}), ...(response?.content?.smartChips ?? {}) };
          const scValuesObj = Object.keys(response?.content?.smartChips).reduce((acc, key) => {
            acc[key] = response?.content?.smartChips[key]?.attributes?.default;
            return acc;
          }, {});
          setSmartChipResponses((prev) => ({ ...prev, ...scValuesObj }));
          updatedFormData = mergeSmartChipsIntoFormData(baseFormData, response?.content?.smartChips);
        } else {
          // Fallback to old merge logic for backward compatibility
          updatedFormData = mergeWorkflowVariableProperties(formData, response.content);
        }
        setRealTimeFormData(updatedFormData);
      });
    } else {
      // If no formId, use the formData as is
      setRealTimeFormData(formData);
    }
  }, [formId, formData]);

  /**
   * Merge workflow variable properties from API response with existing form data
   * @param {Object} currentFormData - Current form data from props
   * @param {Object} apiFormData - Form data from API response
   * @returns {Object} Merged form data with updated workflow variable properties
   */
  const mergeWorkflowVariableProperties = (currentFormData, apiFormData) => {
    if (!currentFormData || !apiFormData) return currentFormData;

    return {
      ...currentFormData,
      subsections: currentFormData.subsections?.map((subsection) => ({
        ...subsection,
        elements: subsection.elements?.map((element) => {
          let updatedElement = { ...element };

          // If this element is a workflow variable, find its updated properties from API
          if (element.properties?.isWorkflowVariable && element.properties?.workflowVariableId) {
            const foundElement = findElementInApiData(apiFormData, element.properties.workflowVariableId);
            if (foundElement) {
              updatedElement = {
                ...updatedElement,
                properties: {
                  ...updatedElement.properties,
                  ...foundElement.properties,
                  showChipName: foundElement.properties?.showChipName === false || updatedElement.properties?.showChipName === false ? false : true,
                },
              };
            }
          }

          // If this element is a row-container, also merge properties for grid elements
          if (element.type === "row-container" && element.gridElements) {
            const updatedGridElements = element.gridElements.map((row) =>
              row.map((gridElement) => {
                if (gridElement && gridElement.properties?.isWorkflowVariable && gridElement.properties?.workflowVariableId) {
                  const updatedGridElement = findElementInApiData(apiFormData, gridElement.properties.workflowVariableId);
                  if (updatedGridElement) {
                    return {
                      ...gridElement,
                      properties: {
                        ...gridElement.properties,
                        ...updatedGridElement.properties,
                        showChipName:
                          updatedGridElement.properties?.showChipName === false || gridElement.properties?.showChipName === false ? false : true,
                      },
                    };
                  }
                }
                return gridElement;
              })
            );

            updatedElement = {
              ...updatedElement,
              gridElements: updatedGridElements,
            };
          }

          return updatedElement;
        }),
      })),
    };
  };

  /**
   * Find element with matching workflow variable ID in API data
   * @param {Object} apiFormData - Form data from API
   * @param {string} workflowVariableId - ID to search for
   * @returns {Object|null} Found element or null
   */
  const findElementInApiData = (apiFormData, workflowVariableId) => {
    if (!apiFormData?.subsections) return null;

    for (const subsection of apiFormData.subsections) {
      if (subsection.elements) {
        for (const element of subsection.elements) {
          // Check regular elements
          if (element.properties?.workflowVariableId === workflowVariableId) {
            return element;
          }

          // Check grid elements inside row containers
          if (element.type === "row-container" && element.gridElements) {
            for (const row of element.gridElements) {
              for (const gridElement of row) {
                if (gridElement && gridElement.properties?.workflowVariableId === workflowVariableId) {
                  return gridElement;
                }
              }
            }
          }
        }
      }
    }
    return null;
  };

  const formRendererElement = (
    <FormRenderer
      formData={realTimeFormData}
      smartChipResponses={smartChipResponses}
      setSmartChipResponses={setSmartChipResponses}
      formResponses={formResponses}
      onFieldChange={handleFieldChange}
      isInteractive={true}
      formTab={formTab}
    />
  );

  const renderFormPreviewContent = () => {
    if (formTab?.isHiddenSection) {
      return (
        <CommonTabStrip
          tabs={[
            { title: FORM_TABS.SUBSECTIONS_OBJ.title, content: formRendererElement },
            { title: FORM_TABS.HIDDEN_SUBSECTIONS_OBJ.title, content: formRendererElement },
          ]}
          selectedTab={formTab.selected}
          onTabSelect={(e) =>
            setFormTab({
              ...formTab,
              selected: e.selected,
              ...FORM_TABS[e.selected === 0 ? "SUBSECTIONS_OBJ" : "HIDDEN_SUBSECTIONS_OBJ"],
            })
          }
          contentClassName='preview-body'
          animation={false}
          tabStripProps={{ keepTabsMounted: true, renderAllContent: true }}
        />
      );
    }
    return formRendererElement;
  };

  return (
    <Dialog
      onClose={onClose}
      title={
        <div className='preview-dialog-title d-flex align-items-center'>
          {icon.VISIBILITY}
          <span className='form-renderer-title'>{formData.title}</span>
          <span className='preview-mode-text'>Preview Mode - Read Only</span>
        </div>
      }
      width={1000}
      height={"100%"}
      className='form-preview-modal'>
      <div className='preview-content d-flex flex-column overflow-hidden'>
        <div className='preview-body'>{renderFormPreviewContent()}</div>
      </div>

      <DialogActionsBar>
        <div className='d-flex justify-content-end w-50'>
          <button onClick={onClose} className='btn btn-sm btn-ghost'>
            Close
          </button>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

const FormPreview = (props) => {
  return (
    <FormCalculationProvider>
      <FormPreviewContent {...props} />
    </FormCalculationProvider>
  );
};

export default FormPreview;
